import { gql } from '@apollo/client';

/**
 * Reach → Analytics visits: people who reached the site from a link in a
 * Reach post, from the site's Google Analytics as last synced
 * (smagent-apiserver reach/services/siteVisits.js). Every post link carries
 * the post's id (utm_content), so visits add up per post and per channel.
 */

export const REACH_SITE_VISITS = gql`
  query ReachSiteVisits($accountId: ID!, $days: Int!) {
    reachSiteVisits(accountId: $accountId, days: $days)
  }
`;

/** The calendar's posted cards: visits and sign-ups for each post, all time. */
export const REACH_POST_VISITS = gql`
  query ReachPostVisits($accountId: ID!, $postIds: [ID!]!) {
    reachPostVisits(accountId: $accountId, postIds: $postIds)
  }
`;

/** A campaign's results, by channel and by post. */
export const REACH_CAMPAIGN_RESULTS = gql`
  query ReachCampaignResults($campaignId: ID!) {
    reachCampaignResults(campaignId: $campaignId)
  }
`;

export interface PostVisits {
  connected: boolean;
  syncedAt: string | null;
  /** views/engagements: to date, from the network; null where it reports none. */
  posts: Array<{
    postId: string;
    visits: number;
    signups: number;
    views: number | null;
    engagements: number | null;
  }>;
}

export interface CampaignResults {
  connected: boolean;
  syncedAt: string | null;
  /** 30 for an always-on campaign (the last 30 days); null: everything its posts did. */
  window: number | null;
  posts: number;
  /** Views on the networks; null when none of the posts' networks reports them. */
  impressions: number | null;
  clicks: number;
  signups: number;
  /** Sign-ups ÷ clicks, null with no clicks. */
  conversion: number | null;
  byChannel: Array<{
    socialProfileId: string;
    platform: string;
    posts: number;
    views: number | null;
    visits: number;
    signups: number;
    conversion: number | null;
  }>;
  byPost: Array<{
    postId: string;
    platform: string;
    socialProfileId: string;
    text: string;
    at: string | null;
    views: number | null;
    visits: number;
    signups: number;
    conversion: number | null;
  }>;
}

/** "950", "18.4K", "1.2M": a network's count, short. */
export const compact = (n: number) =>
  n >= 1_000_000
    ? `${(Math.round(n / 100_000) / 10).toLocaleString()}M`
    : n >= 10_000
      ? `${(Math.round(n / 100) / 10).toLocaleString()}K`
      : n.toLocaleString();

/** "10.8%", or "—" with nothing to divide by. */
export const pct = (r: number | null | undefined) =>
  r == null ? '—' : `${(Math.round(r * 1000) / 10).toLocaleString()}%`;

export interface ReachVisits {
  connected: boolean;
  status: 'syncing' | 'active' | 'reconnect' | null;
  syncedAt: string | null;
  from: string;
  to: string;
  days: number;
  visits: { count: number; previous: number };
  /** Form sign-ups by visitors who came from a post (no Google Analytics needed). */
  signups: { count: number; previous: number };
  /** Views on the networks in the span (the app's "Impressions"), and engagement. */
  impressions: { count: number; previous: number };
  engagements: { count: number; previous: number };
  /** Some network reported views or engagement for this span or the one before. */
  viewsReported: boolean;
  /**
   * Views can be reported here (set up, and a channel on a network that
   * reports them): with none reported, the span had none, so 0, not grey.
   * Missing from an older API server.
   */
  viewsTracked?: boolean;
  /** Networks that report views ("facebook", "instagram", …). */
  viewNetworks?: string[];
  /** Views gained per day, the span and the one before (oldest first). */
  dailyViews: Array<{ day: string; views: number }>;
  previousDailyViews: Array<{ day: string; views: number }>;
  channelViews: Array<{
    socialProfileId: string;
    platform: string;
    views: number;
    engagements: number;
  }>;
  /** One per day of the span: visits by network ("facebook", "x", …). */
  daily: Array<{ day: string; bySource: Record<string, number> }>;
  channels: Array<{ socialProfileId: string; platform: string; visits: number; signups: number }>;
  /** Social-tagged visits and sign-ups that name no post of this site's. */
  untracked: Array<{ source: string; visits: number; signups: number }>;
  posts: Array<{
    postId: string;
    platform: string;
    socialProfileId: string;
    text: string;
    at: string | null;
    visits: number;
    visitors: number;
    signups: number;
    /** To date, from the network; null where it reports none. */
    views: number | null;
    engagements: number | null;
  }>;
}

const shortDay = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString([], { month: 'short', day: 'numeric' });

/**
 * The chart's bars: one per day, or per week over 90 days (the newest week
 * ends on the last day), each with its visits by network.
 */
export function visitBars(daily: ReachVisits['daily'], days: 7 | 28 | 90) {
  const one = (group: ReachVisits['daily'], label: string) => {
    const bySource: Record<string, number> = {};
    for (const d of group) {
      for (const [s, n] of Object.entries(d.bySource)) bySource[s] = (bySource[s] || 0) + n;
    }
    const total = Object.values(bySource).reduce((a, b) => a + b, 0);
    return { day: group[0].day, label, bySource, total };
  };
  if (days !== 90) return daily.map((d) => one([d], shortDay(d.day)));
  const out: Array<ReturnType<typeof one>> = [];
  for (let end = daily.length; end > 0; end -= 7) {
    const week = daily.slice(Math.max(0, end - 7), end);
    out.unshift(one(week, `Week of ${shortDay(week[0].day)}`));
  }
  return out;
}

/** The networks in the span, most visits first. */
export function visitSources(daily: ReachVisits['daily']) {
  const sum: Record<string, number> = {};
  for (const d of daily) {
    for (const [s, n] of Object.entries(d.bySource)) sum[s] = (sum[s] || 0) + n;
  }
  return Object.keys(sum).sort((a, b) => sum[b] - sum[a]);
}

/** "+21% vs previous 7 days", "None in the previous 7 days". */
export function visitChange(v: { count: number; previous: number }, days: number) {
  if (!v.previous) return { lead: '', rest: `None in the previous ${days} days`, tone: 'up' };
  const pct = Math.round(((v.count - v.previous) / v.previous) * 100);
  if (pct === 0) return { lead: 'Same', rest: ` as the previous ${days} days`, tone: 'up' };
  return {
    lead: `${pct > 0 ? '+' : '−'}${Math.abs(pct)}%`,
    rest: ` vs previous ${days} days`,
    tone: pct > 0 ? 'up' : 'down',
  };
}

/**
 * Views to show for a span: the reported numbers; zeros when views can be
 * reported but none were (backlog f89f0ce4, 0afe2770); null when they can't be
 * here, which keeps the grey shapes.
 */
export function viewsShown(v: ReachVisits | null | undefined): boolean {
  return Boolean(v && (v.viewsReported || v.viewsTracked));
}

/** Whether a channel's network reports views to tinycms. */
export function reportsViews(v: ReachVisits | null | undefined, platform: string): boolean {
  return (v?.viewNetworks || []).includes(String(platform || '').toLowerCase());
}
